<template>
  <section id="staff-hours-overview">
    <b-card class="mb-2">
      <b-row class="align-items-end">
        <b-col
          xl="2"
          md="3"
        >
          <b-form-group
            :label="$t('staffHours.month')"
            label-for="overview-month"
          >
            <flat-pickr
              id="overview-month"
              v-model="month"
              class="form-control"
              :config="monthConfig"
              @on-change="$nextTick(load)"
            />
          </b-form-group>
        </b-col>
        <b-col
          xl="2"
          md="3"
        >
          <b-form-group>
            <b-form-checkbox
              v-model="payrollOnly"
              switch
            >
              {{ $t('staffHours.payrollOnly') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col
          xl="3"
          md="3"
        >
          <b-form-group>
            <b-button
              variant="primary"
              :disabled="exporting || !month"
              @click="exportXlsx"
            >
              <feather-icon
                icon="DownloadIcon"
                size="14"
              /> {{ $t('staffHours.export') }}
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <b-overlay
      :show="loading"
      spinner-variant="primary"
      variant="transparent"
      blur="3px"
      rounded="sm"
    >
      <b-row
        v-if="totals"
        class="mb-2"
      >
        <b-col
          v-for="s in summary"
          :key="s.label"
          md="3"
          cols="6"
        >
          <b-card class="mb-1 text-center">
            <h3 class="mb-0">
              {{ s.value }}
            </h3>
            <small class="text-muted">{{ s.label }}</small>
          </b-card>
        </b-col>
      </b-row>

      <b-card
        class="mb-2"
        no-body
      >
        <staff-hours-table
          :rows="visibleRows"
          @show-warnings="warningRowKey = $event"
        />
      </b-card>

      <staff-hours-warnings
        :warnings="visibleWarnings"
        :row-key="warningRowKey"
        @clear-filter="warningRowKey = null"
      />
    </b-overlay>
  </section>
</template>

<script>
import {
  BButton, BCard, BCol, BFormCheckbox, BFormGroup, BOverlay, BRow,
} from 'bootstrap-vue'
import axios from 'axios'
import flatPickr from 'vue-flatpickr-component'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import StaffHoursTable from './StaffHoursTable.vue'
import StaffHoursWarnings from './StaffHoursWarnings.vue'

const API = `${process.env.VUE_APP_ROOT_API}/staff-hours-overview/`

function previousMonth() {
  const d = new Date()
  const p = new Date(d.getFullYear(), d.getMonth() - 1, 1)
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}`
}

export default {
  components: {
    BButton, BCard, BCol, BFormCheckbox, BFormGroup, BOverlay, BRow, flatPickr, StaffHoursTable, StaffHoursWarnings,
  },
  data() {
    return {
      month: previousMonth(),
      // Any picked day maps to its month; the API gets YYYY-MM.
      monthConfig: { dateFormat: 'Y-m', altInput: true, altFormat: 'F Y' },
      payrollOnly: false,
      loading: false,
      exporting: false,
      rows: [],
      warnings: [],
      totals: null,
      warningRowKey: null,
    }
  },
  computed: {
    visibleRows() {
      return this.payrollOnly ? this.rows.filter(r => r.is_payroll) : this.rows
    },
    visibleWarnings() {
      const keys = new Set(this.visibleRows.map(r => r.key))
      return this.warnings.filter(w => keys.has(w.row_key))
    },
    summary() {
      const t = this.totals
      const hours = m => `${(m / 60).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`
      const rows = this.visibleRows
      const sum = f => rows.reduce((a, r) => a + Number(r[f]), 0)
      const cost = rows.reduce((a, r) => a + Number(r.cost), 0)
      return [
        { label: this.$t('staffHours.summary.employees'), value: rows.length },
        { label: this.$t('staffHours.summary.worked'), value: hours(this.payrollOnly ? sum('worked_minutes') : t.worked_minutes) },
        { label: this.$t('staffHours.summary.paidAbsence'), value: hours(this.payrollOnly ? sum('paid_absence_minutes') : t.paid_absence_minutes) },
        {
          label: this.$t('staffHours.summary.cost'),
          value: `€ ${(this.payrollOnly ? cost : Number(t.cost)).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        },
      ]
    },
  },
  watch: {
    payrollOnly() {
      this.warningRowKey = null
    },
  },
  created() {
    this.load()
  },
  methods: {
    authHeaders() {
      return { Authorization: `JWT ${this.$store.state.jwt}` }
    },
    toast(variant, title) {
      this.$toast({ component: ToastificationContent, props: { title, icon: 'AlertCircleIcon', variant } })
    },
    handleError(error, message) {
      if (error.response && error.response.status === 401) {
        this.$router.push({ name: 'login' })
      } else if (error.response && error.response.status === 403) {
        this.toast('danger', this.$t('staffHours.forbidden'))
      } else {
        this.toast('danger', message)
      }
    },
    async load() {
      if (!this.month) return
      this.loading = true
      try {
        const response = await axios.get(API, { headers: this.authHeaders(), params: { month: this.month } })
        this.rows = response.data.rows
        this.warnings = response.data.warnings
        this.totals = response.data.totals
        this.warningRowKey = null
      } catch (error) {
        this.handleError(error, this.$t('staffHours.loadFailed'))
      } finally {
        this.loading = false
      }
    },
    async exportXlsx() {
      this.exporting = true
      try {
        const response = await axios.get(`${API}export/`, {
          headers: this.authHeaders(),
          params: { month: this.month },
          responseType: 'blob',
        })
        const match = /filename="?([^";]+)"?/.exec(response.headers['content-disposition'] || '')
        const filename = match ? match[1] : `stunden_${this.month}.xlsx`
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.handleError(error, this.$t('staffHours.exportFailed'))
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
